import React, {
    Fragment,
    PropsWithChildren,
    useContext,
    useMemo,
    useState,
} from "react";
import { defineMessages, useIntl } from "react-intl";
import MobileContainer from "./Components/MobileContainer";
import {
    HierarchyPath,
    TreeNode,
} from "../../../../../../components/TreeCheckboxGroup";
import { playlistMessages } from "../playlistMessages";

import { useFeatureFlag } from "@evidenceb/athena-common/modules/FeatureFlags";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import StudentPicker from "../../../../../DashBoard/DashboardTeacher/Group/Components/StudentPicker/StudentPicker";
import ContainerBlock from "../../../../../../design-system-components/ContainerBlock/ContainerBlock";
import {
    Control,
    SegmentedControl,
} from "../../../../../../design-system-components/SegmentedControl/SegmentedControl";
import { sessionStore } from "../../../../../../contexts/SessionContext";
import { useGroups } from "../../../../../DashBoard/DashboardTeacher/Group/api/useQueries";
import { LegacyLoader } from "../../../../../../components/Loader/Loader";

import "./TeacherPlaylistSend.scss";

type Props = {
    students: Record<string, TreeNode>;
    checkedStudents: HierarchyPath[];
    toggleStudents: (path: HierarchyPath) => void;
};

const TeacherPlaylistSend = ({
    students,
    checkedStudents,
    toggleStudents,
}: Props) => {
    const intl = useIntl();
    const { isTabletOrSmaller } = useBreakpoints();
    const groupsFlag = useFeatureFlag("groupManagement");

    const Container = useMemo(
        () =>
            isTabletOrSmaller
                ? withLabelMobileContainer(
                      intl.formatMessage(playlistMessages.recipients)
                  )
                : Fragment,
        [intl, isTabletOrSmaller]
    );
    const Block = groupsFlag ? ContainerBlock : "div";

    const [currentTab, setCurrentTab] = useState<StudentsPartition>(
        StudentsPartition.Classroom
    );

    const {
        session: { classrooms },
    } = useContext(sessionStore);
    const groupsQuery = useGroups();
    const groups = groupsQuery.data;
    const classroomIds =
        currentTab === StudentsPartition.Classroom
            ? classrooms?.map(({ id }) => id)
            : groups?.map(({ id }) => id);

    const isLoading =
        currentTab === StudentsPartition.Group && groupsQuery.isLoading;
    const isError =
        currentTab === StudentsPartition.Group && groupsQuery.isError;
    const showPicker = classroomIds && !isLoading && !isError;

    return (
        <Container>
            <Block className="teacher-playlist-send__container">
                {groupsFlag && (
                    <SegmentedControl
                        variant="secondary"
                        className="teacher-playlist-send__control"
                        value={currentTab}
                        onChange={(value) =>
                            setCurrentTab(value as StudentsPartition)
                        }
                    >
                        <Control value={StudentsPartition.Classroom}>
                            {intl.formatMessage(messages.classroomsControl)}
                        </Control>
                        <Control value={StudentsPartition.Group}>
                            {intl.formatMessage(messages.groupsControl)}
                        </Control>
                    </SegmentedControl>
                )}

                {showPicker && (
                    <StudentPicker
                        students={students}
                        classroomIds={classroomIds}
                        pickedStudents={checkedStudents}
                        onStudentPick={toggleStudents}
                    />
                )}
                {isLoading && <LegacyLoader />}
                {isError && (
                    <p>{intl.formatMessage(messages.cannotRetrieveGroups)}</p>
                )}
            </Block>
        </Container>
    );
};
export default TeacherPlaylistSend;

enum StudentsPartition {
    Classroom = "classrooms",
    Group = "groups",
}

const withLabelMobileContainer =
    (label: string) =>
    ({ children }: PropsWithChildren) =>
        <MobileContainer label={label}>{children}</MobileContainer>;

const messages = defineMessages({
    classroomsControl: {
        id: "assignment-classroomControlLabel",
        defaultMessage: "Classrooms",
    },
    groupsControl: {
        id: "assignmemt-groupControlLabel",
        defaultMessage: "Groups",
    },
    cannotRetrieveGroups: {
        id: "assignment-error-cannotRetrieveGroups",
        defaultMessage: "We were unable to retrieve your groups.",
    },
});
